import React, {
  useEffect,
  useState,
  useDeferredValue,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Badge, Button, Dropdown, Popover, Spin, Typography } from "antd";
import { useAppMessage } from "../../hooks/useAppMessage";
import {
  RetweetOutlined,
  GroupOutlined,
  FilterFilled,
  ExportOutlined,
  RedoOutlined,
  SearchOutlined,
  // DollarOutlined,
  FileSearchOutlined,
  EllipsisOutlined,
  PushpinOutlined,
} from "@ant-design/icons";
import DndTable from "../../Components/DndTable/DndTable";
import { SelectColumns } from "../../Components/modals/SelectColumns";
import { FreezeColumns } from "../../Components/modals/FreezeColumns";
import { AddComments } from "../../Components/modals/AddComments";
import { ViewComments } from "../../Components/modals/ViewComments";
import { RankingFilters } from "../../Components/modals/RankingFilter";
import { MoveRank } from "../../Components/modals/MoveRank";
import { Helmet } from "react-helmet";
import { RankingAPI } from "../../API/RankingAPI";
// import CostSavings from "../../Components/modals/CostSavings";
import OptimisationReview from "../../Components/modals/OptimisationReview";
import HeaderTab from "../../Components/headerTab/HeaderTab";
import { ConfigurationAPI } from "../../API/ConfigurationAPI";
import BottomTab from "../../Components/BottomTab/BottomTab";
import { useFlightModel } from "../../contexts/FlightModelContext";
import { extractPreferences, fromApiColumnLayoutKey } from "../../Components/DndTable/gridPreferencesUtils";
import { getRankingGridColumnKeys, fromApiSortColumnKey, toApiSortColumnKey, DEFAULT_RANKING_SORT_BY, DEFAULT_RANKING_SORT_DIR, getNextSortState, isSortableColumnKey, RANKING_GRID_DEFAULT_WIDTHS, getColumnDisplayLabel } from "./RankingsUtils";
const { Text } = Typography;

export const Rankings = () => {
  const [messageApi, contextHolder] = useAppMessage();
  const [spinning, setSpinning] = useState(false);
  const [rankingData, setRankingData] = useState([]);
  const [openselectColumns, setOpenSelectColumns] = useState(false);
  const [openFreezeColumns, setOpenFreezeColumns] = useState(false);
  const [gridPreferencesVersion, setGridPreferencesVersion] = useState(0);
  const [openAddComments, setOpenAddComments] = useState(false);
  const [openViewComments, setOpenViewComments] = useState(false);
  const [openRankingFilter, setOpenRankingFilter] = useState(false);
  const [openMoveRank, setOpenMoveRank] = useState(false);
  // const [openCostSavings, setOpenCostSavings] = useState(false);
  const [openOptimisationReview, setOpenOptimisationReview] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [allGridColumnKeys, setAllGridColumnKeys] = useState([]);
  const [selectedID, setSelectedID] = useState("");
  const [selectedRow, setSelectedRow] = useState("");
  const [dragableState, setDragableSatate] = useState(true);
  const [serachInput, setSearchInput] = useState("");
  const [defualtData, setDefualtData] = useState([]);
  const [lastFile, setLastFile] = useState("");
  const [selectedRowKeysar, setSelectedRowKeys] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [filteredValues, setFilteredvalues] = useState({
    cat: "",
    status: [],
    task: [],
    specType: "",
    // yieldValue: 90,
    yieldValueFrom: 0,
    yieldValueTo: 100,
    maintenanceCheck: "",
    maintenanceCheckPackage: "",
    maintenanceCheckInterval: "",
    maintenanceCheckPackageInterval: "",
  });
  const [filterStatus, setFilterStatus] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  // Highest rank a spec can be moved to. The list is server-paged, so the rows
  // in hand only cover the current page -- the ceiling has to come from the API.
  const [maxRank, setMaxRank] = useState(0);
  const [sortBy, setSortBy] = useState(DEFAULT_RANKING_SORT_BY);
  const [sortDir, setSortDir] = useState(DEFAULT_RANKING_SORT_DIR);
   const { selectedFlightModel, refreshFlightModels } = useFlightModel();

  // useDefferedValue
  const deferredQuery = useDeferredValue(serachInput);
  // The first page load is owned by initializeRankings() below, which waits for
  // the saved sort before fetching. Tracking the query this effect last searched
  // for keeps it from firing a second, identical rank-list request on mount
  // while still reacting to every real change of the search box.
  const lastSearchedQueryRef = useRef(serachInput);
  useEffect(() => {
    if (lastSearchedQueryRef.current === deferredQuery) {
      return;
    }
    lastSearchedQueryRef.current = deferredQuery;
    serachFun();
  }, [deferredQuery]);

  const messageToast = useCallback(
    (type, message, key) => {
      messageApi.open({
        type: type,
        content: message,
        duration: 6,
        // A stable key makes repeat calls replace the existing toast instead
        // of stacking identical messages.
        ...(key ? { key } : {}),
      });
    },
    [messageApi],
  );

  const fetchColumnConfig = useCallback(async () => {
    setSpinning(true);
    try {
      const response = await RankingAPI.getColumnConfig();
      if (response?.data?.status === true) {
        const configColumns = response.data.all_columns
          // Only the internal drag handle and the assigned-on timestamp stay
          // out of the managed set. Status, Comments and Assigned To are now
          // reorderable / hideable like any other column.
          .filter(
            (col) =>
              col.column_key !== "is_drag" &&
              col.column_key !== "assigned_on",
          )
          .map((col) => {
            const existCol = response.data?.visible_columns?.find(
              (c) => c.column_key === col.column_key,
            );
            return {
              // Grid uses "comments"; the backend key is "have_comments".
              id: fromApiColumnLayoutKey(col.column_key),
              label:
                col.column_key === "have_comments"
                  ? "Comments"
                  : getColumnDisplayLabel(col.column_key, col.label),
              rank: existCol ? existCol.display_order : null,
            };
          })
          .filter((col) => col.rank !== null)
          .sort((a, b) => {
            const orderA =
              a.rank !== null ? parseInt(a.rank) : Number.MAX_SAFE_INTEGER;
            const orderB =
              b.rank !== null ? parseInt(b.rank) : Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

        setSelectedColumns(configColumns);
        setAllGridColumnKeys(
          getRankingGridColumnKeys(response.data.all_columns),
        );
      }
    } catch (error) {
      messageToast("error", "Failed to fetch column configuration.");
    } finally {
      setSpinning(false);
    }
  }, [messageToast]);

  const loadSortPreferences = useCallback(async () => {
    try {
      const response = await RankingAPI.getRankGridPreferences();
      if (!response || response.status >= 400) {
        return {
          sortBy: DEFAULT_RANKING_SORT_BY,
          sortDir: DEFAULT_RANKING_SORT_DIR,
        };
      }

      const preferences = extractPreferences(response);
      const nextSortBy = preferences?.sort_by
        ? fromApiSortColumnKey(preferences.sort_by)
        : DEFAULT_RANKING_SORT_BY;
      const nextSortDir =
        preferences?.sort_dir !== undefined && preferences?.sort_dir !== null
          ? preferences.sort_dir
          : "";

      setSortBy(nextSortBy);
      setSortDir(nextSortDir);

      return { sortBy: nextSortBy, sortDir: nextSortDir };
    } catch {
      return {
        sortBy: DEFAULT_RANKING_SORT_BY,
        sortDir: DEFAULT_RANKING_SORT_DIR,
      };
    }
  }, []);

  const persistSortPreferences = useCallback(async (nextSortBy, nextSortDir) => {
    try {
      const preferencesResponse = await RankingAPI.getRankGridPreferences();
      const preferences = extractPreferences(preferencesResponse);

      await RankingAPI.updateRankGridPreferences({
        sort_by: toApiSortColumnKey(nextSortBy),
        sort_dir: nextSortDir,
        column_layout: preferences.column_layout,
      });
    } catch {
      // Keep the in-session sort even if preference save fails.
    }
  }, []);

  const getRankingData = useCallback(
    async function getRankingData(
      pageNo = page,
      perPage = pageSize,
      reset = false,
      sortOverrides = {},
    ) {
      setSpinning(true);
        let flightModelToUse = selectedFlightModel;

    // fallback from context
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      return;
    }

      const activeSortBy = sortOverrides.sortBy ?? sortBy;
      const activeSortDir = sortOverrides.sortDir ?? sortDir;

      // Reset has to ask the server for the *unfiltered* list. The request was
      // built from the live filter state, so it came back still filtered while
      // the reset branch below cleared only the filter UI -- leaving the grid
      // showing filtered rows (or "No Records Found") under a cleared badge.
      // Only the reset path changes here; reset === false behaves as before.
      const activeFilters = reset
        ? {
            cat: "",
            status: [],
            task: [],
            specType: "",
            yieldValueFrom: 0,
            yieldValueTo: 100,
            maintenanceCheck: "",
            maintenanceCheckPackage: "",
            maintenanceCheckInterval: "",
            maintenanceCheckPackageInterval: "",
          }
        : filteredValues;
      const activeSearch = reset ? "" : serachInput;

      const queryParams = {
        params: {
          flight_model:flightModelToUse,
          //  localStorage.getItem("flight_model"),
          page: pageNo,
          page_size: perPage,
          sort_by: toApiSortColumnKey(activeSortBy),
          sort_dir: activeSortDir,
          spec_type: activeFilters.specType,
          status: activeFilters.status.map((ele) => ele.trim()).join(","),
          mntc_check: activeFilters.maintenanceCheck,
          mntc_check_package: activeFilters.maintenanceCheckPackage,
          mntc_check_interval: activeFilters.maintenanceCheckInterval,
          mntc_check_package_interval:
            activeFilters.maintenanceCheckPackageInterval,
          yield_min: activeFilters.yieldValueFrom,
          yield_max: activeFilters.yieldValueTo,
          fec: activeFilters.cat.trim(),
          task_type: activeFilters.task.map((ele) => ele.trim()).join(","),
          search: activeSearch.trim(),
        },
      };

      await RankingAPI.getRannkList(queryParams)
        .then((res) => {
          if (res.data.status === true) {
            if (reset === false) {
              setRankingData(res.data.data);

              const hasFilter =
                filteredValues?.cat?.trim() !== "" ||
                filteredValues?.specType?.trim() !== "" ||
                filteredValues?.status?.length > 0 ||
                filteredValues?.task?.length > 0 ||
                filteredValues.yieldValueFrom !== 0 ||
                filteredValues.yieldValueTo !== 100 ||
                filteredValues?.maintenanceCheck?.trim() !== "" ||
                filteredValues?.maintenanceCheckPackage?.trim() !== "" ||
                filteredValues?.maintenanceCheckInterval?.trim() !== "" ||
                filteredValues?.maintenanceCheckPackageInterval?.trim() !==
                  "" ||
                serachInput.trim() !== "";
              if (!hasFilter) {
                setDragableSatate(true);
              } else {
                setDragableSatate(false);
              }
            } else {
              setSelectedRowKeys([]);
              setSelectedRow({});
              setSearchInput("");
              setFilteredvalues({
                cat: "",
                status: [],
                task: [],
                specType: "",
                // yieldValue: 90,
                yieldValueFrom: 0,
                yieldValueTo: 100,
                maintenanceCheck: "",
                maintenanceCheckPackage: "",
                maintenanceCheckInterval: "",
                maintenanceCheckPackageInterval: "",
              });
              setDragableSatate(true);
              setFilterStatus(false);
              setRankingData(res.data.data);
            }
            setTotalRecords(res.data.total);
            setMaxRank(res.data.max_rank);
            setPage(pageNo);
            setPageSize(perPage);
            if (sortOverrides.sortBy !== undefined) {
              setSortBy(sortOverrides.sortBy);
            }
            if (sortOverrides.sortDir !== undefined) {
              setSortDir(sortOverrides.sortDir);
            }
            setDefualtData(res.data.data);
            const arr = [{ value: null, label: "--select--" }];
            res.data?.employee_details.forEach((ele) => {
              arr.push({
                value: ele.username,
                label: `${ele.first_name} ${ele.last_name}`,
              });
            });
            setAssigneeOptions(arr);
            setSpinning(false);
          } else {
            messageToast("error", res.data?.message);
            setSpinning(false);
          }
        })
        .catch((err) => {
          messageToast("error", "Internal Server Error");
          setSpinning(false);
        })
        .finally(() => {
          // setSpinning(false)
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [page, pageSize, sortBy, sortDir, filteredValues, serachInput, messageToast, selectedFlightModel, refreshFlightModels],
  );

  const handleColumnSort = useCallback(
    (columnKey) => {
      if (!isSortableColumnKey(columnKey)) {
        return;
      }

      const nextSort = getNextSortState(sortBy, sortDir, columnKey);
      setSortBy(nextSort.sortBy);
      setSortDir(nextSort.sortDir);
      setPage(1);
      getRankingData(1, pageSize, false, nextSort);
      persistSortPreferences(nextSort.sortBy, nextSort.sortDir);
    },
    [getRankingData, pageSize, persistSortPreferences, sortBy, sortDir],
  );

  async function resetRankFun() {
    setSpinning(true);
       let flightModelToUse = selectedFlightModel;

    // fallback from context
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      return;
    }
    const queryParams = {
      params: {
        flight_model: flightModelToUse,
        // localStorage.getItem("flight_model"),
      },
    };
    await RankingAPI.resetRank(queryParams)
      .then((res) => {
        if (res.data.status === true) {
          getRankingData(page, pageSize, true);
        } else {
          messageToast("error", res.data?.message);
          setSpinning(false);
        }
      })
      .catch((err) => {
        messageToast("error", "Internal Server Error");
        setSpinning(false);
      });
    // .finally(() => setSpinning(false))
  }

  const loadConfigData = useCallback(
    async function loadConfigData() {
         let flightModelToUse = selectedFlightModel;

    // fallback from context
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      return;
    }
      const queryParams = {
        params: {
          flight_model:flightModelToUse,
          //  localStorage.getItem("flight_model"),
        },
      };
      await ConfigurationAPI.getCofigData(queryParams)
        .then((res) => {
          if (res.data.status === true) {
            let strDate = "";
            if (res.data?.data?.last_file?.uploaded_on) {
              strDate = new Date(res.data?.data?.last_file?.uploaded_on);
            }
            setLastFile(String(strDate).slice(4, 15));
          } else {
            messageToast("error", res.data?.message);
          }
        })
        .catch((err) => {
          messageToast("error", "Internal Server Error");
        });
    },
    [messageToast],
  );

  useEffect(() => {
    let mounted = true;

    const initializeRankings = async () => {
      setSpinning(true);
      fetchColumnConfig();
      loadConfigData();

      const initialSort = await loadSortPreferences();
      if (!mounted) return;

      getRankingData(1, pageSize, false, initialSort);
    };

    initializeRankings();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchColumnConfig, loadConfigData, loadSortPreferences]);

  async function exportExcel() {
    setSpinning(true);
    try {
         let flightModelToUse = selectedFlightModel;

    // fallback from context
    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");setSpinning(false);
      return;
    }
      const queryParams = {
        params: {
          flight_model:flightModelToUse,
          //  localStorage.getItem("flight_model"),
          spec_type: filteredValues.specType,
          status: filteredValues.status.map((ele) => ele.trim()).join(","),
          mntc_check: filteredValues.maintenanceCheck,
          mntc_check_package: filteredValues.maintenanceCheckPackage,
          mntc_check_interval: filteredValues.maintenanceCheckInterval,
          mntc_check_package_interval:
            filteredValues.maintenanceCheckPackageInterval,
          yield_min: filteredValues.yieldValueFrom,
          yield_max: filteredValues.yieldValueTo,
          fec: filteredValues.cat.trim(),
          task_type: filteredValues.task.map((ele) => ele.trim()).join(","),
          search: serachInput.trim(),
          export: true,
        },
      };

      const response = await RankingAPI.exportRankList(queryParams);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Extract filename from content-disposition header if provided by backend
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "Ranking_export.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=(['"]?)([^'"\n;]*)\1/,
        );
        if (match?.[2]) fileName = match[2].trim();
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      messageToast("error", "Export failed. Please try again.");
    } finally {
      setSpinning(false);
    }
  }

  async function serachFun() {
    setSelectedRowKeys([]);
    setSelectedRow({});
    setSpinning(true);
    getRankingData(1, pageSize, false);
  }

  // Move repositions one row at a time, so a multi-row selection is ambiguous.
  const hasMultipleSelected = selectedRowKeysar.length > 1;
  const canManualRerank =
    dragableState === true && sortBy === "rank" && !hasMultipleSelected;
  const moveDisabledHint = hasMultipleSelected
    ? "Multiple specifications cannot be moved."
    : dragableState !== true
      ? "To change the rank, you must clear all filters applied"
      : "Manual re-ranking is available only when the table is sorted by Rank";

  const optimisationSelectedRows = useMemo(
    () =>
      rankingData.filter((row) =>
        selectedRowKeysar.includes(row.key) ||
        selectedRowKeysar.includes(row.id) ||
        selectedRowKeysar.includes(String(row.id)) ||
        selectedRowKeysar.includes(row.ranked_id) ||
        selectedRowKeysar.includes(String(row.ranked_id))
      ),
    [rankingData, selectedRowKeysar],
  );

  const buildSelectedRowPayload = (row, keys) => ({
    id: row?.id,
    key: keys,
    spec: row?.spec_concat,
    rank: row?.rank,
    repeat_val: row?.repeat_val,
    repeat_max: row?.repeat_max,
    initial_max: row?.initial_max,
    total_labor_cost_max: row?.total_labor_cost_max,
    interval_type: row?.interval_type,
    total_effort_hours: row?.total_effort_hours,
    yield_percentage: row?.yield_percentage,
  });

  const handleOptimisationFilterApply = (rows) => {
    // Keep the same record-based key derivation the grid uses, so a key is
    // never a page-relative position (see DndTable's dataSource effect).
    const keyedRows = (rows || []).map((row, index) => ({
      ...row,
      key: String(
        row?.id ?? row?.ranked_id ?? row?.spec_concat ?? `row-${index + 1}`,
      ),
    }));

    setRankingData(keyedRows);
    setTotalRecords(keyedRows.length);
    setPage(1);

    setSelectedRowKeys([]);
    setSelectedRow({});
    localStorage.setItem("selectedRow", "");
  };

  return (
    <div className="container_div">
      <Helmet>
        <title>SpecOptimizer | Ranking</title>
      </Helmet>
      <HeaderTab currrentTab="ranking" />

      <Spin spinning={spinning}>
        <div className="box_wraper_div">
          <div className="table_head_wraper">
            <Text className="table_head">
             Ranking for the month of{" "}
{new Date().toLocaleString("default", { month: "long" })}
            </Text>
            <div className="table_custom_buttons">
              <div
                style={{
                  borderRadius: "25px",
                  backgroundColor: "#EBEEF8",
                  color: "#737373",
                  display: "flex",
                }}
              >
                <input
                  value={serachInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="searc_input"
                />
                <SearchOutlined
                  className="search_btn"
                  style={{
                    color: !serachInput ? "#737373" : "#ff4d4f",
                    fontSize: "18px",
                    padding: "10px",
                    borderRadius: "6px",
                  }}
                />
              </div>
              {canManualRerank ? (
                <Button
                  onClick={() => setOpenMoveRank(true)}
                  type="primary"
                  style={{ flex: "1 1 0px " }}
                  icon={<RetweetOutlined />}
                >
                  Move
                </Button>
              ) : (
                <Popover
                  title=""
                  content={
                    <p
                      style={{
                        fontWeight: 500,
                        color: "#565656",
                        fontSize: "15px",
                        lineHeight: "1.5rem",
                        margin: 0,
                      }}
                    >
                      {moveDisabledHint}
                    </p>
                  }
                >
                  <span style={{ flex: "1 1 0px", display: "inline-block" }}>
                    <Button
                      disabled
                      type="primary"
                      style={{ width: "100%" }}
                      icon={<RetweetOutlined />}
                    >
                      Move
                    </Button>
                  </span>
                </Popover>
              )}
              {/* Cost Savings button kept but hidden
              <Button
                onClick={() => {
                  if (
                    selectedRow?.id &&
                    selectedRow?.id !== "" &&
                    selectedRow?.id !== null
                  ) {
                    setOpenCostSavings(true);
                  } else {
                    messageToast(
                      "warning",
                      "Please select a spec to view cost savings",
                    );
                  }
                }}
                type="primary"
                style={{ flex: "1 1 0px " }}
                icon={<DollarOutlined />}
              >
                Cost Savings
              </Button>
              */}
              <Button
                onClick={() => setOpenOptimisationReview(true)}
                type="primary"
                style={{ flex: "1 1 0px " }}
                icon={<FileSearchOutlined />}
              >
                Optimization Review
              </Button>
              <Dropdown
                placement="bottomRight"
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <div
                          onClick={() => setOpenSelectColumns(true)}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <GroupOutlined />{" "}
                          <Text style={{ marginLeft: "10px" }}>Columns</Text>
                        </div>
                      ),
                    },
                    {
                      key: "2",
                      label: (
                        <div
                          onClick={() => setOpenFreezeColumns(true)}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <PushpinOutlined />{" "}
                          <Text style={{ marginLeft: "10px" }}>Freezing</Text>
                        </div>
                      ),
                    },
                    {
                      key: "3",
                      label: (
                        <div
                          onClick={() => setOpenRankingFilter(true)}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <Badge dot={filterStatus}>
                            <FilterFilled />{" "}
                          </Badge>{" "}
                          <Text style={{ marginLeft: "10px" }}>Filter</Text>
                        </div>
                      ),
                    },
                    {
                      key: "4",
                      label: (
                        <div
                          onClick={exportExcel}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <ExportOutlined />{" "}
                          <Text style={{ marginLeft: "10px" }}>
                            Export to Excel
                          </Text>
                        </div>
                      ),
                    },
                    {
                      key: "5",
                      label: (
                        <div
                          onClick={() => resetRankFun()}
                          style={{ fontSize: "18px", minWidth: "150px" }}
                        >
                          <RedoOutlined />{" "}
                          <Text style={{ marginLeft: "10px" }}>Reset</Text>
                        </div>
                      ),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Badge dot={filterStatus}>
                  <Button
                    // disabled={defualtData.length > 0 ? false : true}
                    type="primary"
                    style={{
                      backgroundColor: "#EBEEF8",
                      color: "#304CB2",
                      flex: "1 1 0px ",
                    }}
                    icon={<EllipsisOutlined />}
                  >
                    Actions
                  </Button>
                </Badge>
              </Dropdown>
            </div>
          </div>
          {selectedRowKeysar.length > 0 ? (
            <div className="ranking-selection-bar">
              <span className="ranking-selection-bar__count">
                {selectedRowKeysar.length} Specification
                {selectedRowKeysar.length === 1 ? "" : "s"} Selected
              </span>
              <button
                type="button"
                className="ranking-selection-bar__clear"
                onClick={() => {
                  setSelectedRowKeys([]);
                  setSelectedRow({});
                  localStorage.setItem("selectedRow", "");
                }}
              >
                Clear Selection
              </button>
            </div>
          ) : (
            <Text className="optimisation-review-helper">
              Select one or more specifications to run an Optimization Review.
            </Text>
          )}

          <DndTable
            listData={rankingData}
            selectedColumns={selectedColumns}
            allGridColumnKeys={allGridColumnKeys}
            setSelectedColumns={setSelectedColumns}
            setOpenAddComments={setOpenAddComments}
            setSelectedID={setSelectedID}
            setOpenViewComments={setOpenViewComments}
            getRankingDataFun={getRankingData}
            setSpinning={setSpinning}
            setSelectedRow={setSelectedRow}
            selectedRow={selectedRow}
            dragableState={dragableState}
            messageToast={messageToast}
            selectedRowKeysar={selectedRowKeysar}
            setSelectedRowKeys={setSelectedRowKeys}
            assigneeOptions={assigneeOptions}
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            filteredValues={filteredValues}
            serachInput={serachInput}
            setPage={setPage}
            setPageSize={setPageSize}
            setTotalRecords={setTotalRecords}
            setRankingData={setRankingData}
            sortBy={sortBy}
            sortDir={sortDir}
            onColumnSort={handleColumnSort}
            gridPreferencesVersion={gridPreferencesVersion}
          />

          <SelectColumns
            open={openselectColumns}
            setOpen={setOpenSelectColumns}
            setSelectedColumns={setSelectedColumns}
            messageToast={messageToast}
          />
          <FreezeColumns
            open={openFreezeColumns}
            setOpen={setOpenFreezeColumns}
            messageToast={messageToast}
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            sortBy={sortBy}
            sortDir={sortDir}
            allGridColumnKeys={allGridColumnKeys}
            defaultWidthMap={RANKING_GRID_DEFAULT_WIDTHS}
            onApplySuccess={() =>
              setGridPreferencesVersion((version) => version + 1)
            }
          />
          <AddComments
            open={openAddComments}
            setOpen={setOpenAddComments}
            selectedID={selectedID}
            listData={rankingData}
            setListData={setRankingData}
            messageToast={messageToast}
            assigneeOptions={assigneeOptions}
          />
          <ViewComments
            open={openViewComments}
            setOpen={setOpenViewComments}
            selectedID={selectedID}
            setOpenAddComments={setOpenAddComments}
            messageToast={messageToast}
          />
          <RankingFilters
            open={openRankingFilter}
            setOpen={setOpenRankingFilter}
            scope="ranking"
            listData={rankingData}
            setListData={setRankingData}
            getRankingDataFun={getRankingData}
            setDragableSatate={setDragableSatate}
            setFilterStatus={setFilterStatus}
            serachInput={serachInput}
            defualtData={defualtData}
            setSearchInput={setSearchInput}
            filteredValues={filteredValues}
            setFilteredvalues={setFilteredvalues}
            setTotalRecords={setTotalRecords}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            messageToast={messageToast}
          />
          <MoveRank
            open={openMoveRank}
            setOpen={setOpenMoveRank}
            selectedRow={selectedRow}
            listData={rankingData}
            maxRank={maxRank}
            getRankingDataFun={getRankingData}
            dragableState={dragableState}
            sortBy={sortBy}
            messageToast={messageToast}
            setSelectedRowKeys={setSelectedRowKeys}
            setSelectedRow={setSelectedRow}
          />
          {/* Cost Savings modal kept but hidden
          <CostSavings
            open={openCostSavings}
            setOpen={setOpenCostSavings}
            selectedRow={selectedRow}
            messageToast={messageToast}
          />
          */}
          <OptimisationReview
            open={openOptimisationReview}
            setOpen={(value) => {
              setOpenOptimisationReview(value);
              if (value === false) {
                // Clear the selection and reload the ranking list so applied
                // updates show live (checked specs are not preserved).
                setSelectedRowKeys([]);
                setSelectedRow({});
                localStorage.setItem("selectedRow", "");
                getRankingData(page, pageSize, false);
              }
            }}
            selectedRows={optimisationSelectedRows}
            assigneeOptions={assigneeOptions}
            onAddSelectedRows={(rows) => {
              setSelectedRowKeys((prev) => {
                const nextKeys = rows.map((row) => {
                  const match = rankingData.find((item) => String(item.id) === String(row.id ?? row.ranked_id));
                  return match?.key ?? row.key ?? row.id ?? row.ranked_id;
                });
                return [...new Set([...prev, ...nextKeys])];
              });
              if (rows[0]) {
                const primary = rows[rows.length - 1];
                setSelectedRow(buildSelectedRowPayload(primary, rows.map((r) => r.key ?? r.id ?? r.ranked_id)));
              }
            }}
            onOptimisationFilterApply={handleOptimisationFilterApply}
            onClearAllSelectedRows={() => {
              setSelectedRowKeys([]);
              setSelectedRow({});
              localStorage.setItem("selectedRow", "");
            }}
            onRemoveSelectedRow={(row) => {
              const rowKey = row?.key ?? row?.id ?? row?.ranked_id;
              setSelectedRowKeys((prev) =>
                prev.filter(
                  (key) =>
                    String(key) !== String(rowKey) &&
                    String(key) !== String(row?.id) &&
                    String(key) !== String(row?.key) &&
                    String(key) !== String(row?.ranked_id),
                ),
              );
              setSelectedRow((prev) =>
                String(prev?.id) === String(row?.id) ||
                String(prev?.ranked_id) === String(row?.ranked_id) ||
                String(prev?.key) === String(rowKey)
                  ? {}
                  : prev,
              );
              if (localStorage.getItem("selectedRow") === String(rowKey)) {
                localStorage.setItem("selectedRow", "");
              }
            }}
            messageToast={messageToast}
          />
        </div>
      </Spin>

      <BottomTab lastFile={lastFile} />
      {contextHolder}
    </div>
  );
};
